import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { PermissionKey, PermissionScope } from '@stoplord/shared';
import { useAuth } from '@/store/auth';
import { useCan } from '@/hooks/usePermission';
import { PermissionDenied, Spinner } from './ui';

/** `portal` routes are only for client users; everything else only for staff. */
export function RequireAuth({ children, portal = false }: { children: ReactNode; portal?: boolean }) {
  const status = useAuth((s) => s.status);
  const mustChange = useAuth((s) => s.user?.mustChangePassword);
  const kind = useAuth((s) => s.user?.kind);
  const loc = useLocation();
  if (status === 'loading') return <div className="flex h-full items-center justify-center"><Spinner label="Starting StopLord" /></div>;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (mustChange && loc.pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  if (kind === 'client' && !portal && loc.pathname !== '/change-password') return <Navigate to="/portal" replace />;
  if (kind !== 'client' && portal) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Route-level permission gate. The API enforces the same rule; this only avoids a dead-end screen. */
/** `anyOf` allows the page when any listed permission is held (perm is then ignored). */
export function RequirePermission({ perm, scope, anyOf, children }: { perm: PermissionKey; scope?: PermissionScope; anyOf?: PermissionKey[]; children: ReactNode }) {
  const can = useCan();
  const ok = anyOf ? anyOf.some((p) => can(p, scope)) : can(perm, scope);
  return ok ? <>{children}</> : <div className="py-10"><PermissionDenied /></div>;
}

export function Can({ perm, scope, children, fallback = null }: { perm: PermissionKey; scope?: PermissionScope; children: ReactNode; fallback?: ReactNode }) {
  const can = useCan();
  return <>{can(perm, scope) ? children : fallback}</>;
}
